import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getQuotesApi, getQuoteApi, createQuoteApi, updateQuoteApi, deleteQuoteApi, uploadQuoteWhatsAppPdfApi } from '../api/quoteApi';
import { generateQuotePDF, generateQuotePDFBlob } from "../utils/generateQuotePDF";
import api from '../api/axios';
import { mapItemMasterRowsToQuotePayload } from '../utils/templateQuoteFromMaster';
import TableSearchInput from '../components/TableSearchInput';
import ColumnFilterInput from '../components/ColumnFilterInput';
import { quoteCardMatch, matchesItemSpaceMaterialLine, normalizeSearch } from '../utils/textSearch';
import { activeQuoteItems, effectiveQuoteLineQty, getQuoteCommercialTotals, quoteLineBase, quoteLineDiscountAmount, quoteLineNetBeforeGst } from '../utils/quoteLineMath';
import { formatInr } from '../utils/formatInr';
import { normalizeQuoteItems } from '../utils/normalizeQuoteItems';

const emptyQuoteLineFilters = () => ({ item: '', space: '', material: '', sectionLabel: '' });

const quoteDiscountLabel = (quote) =>
  String(
    quote?.discountLabel ||
    (quote?.items || []).find((item) => item?._quoteDiscountLabel)?._quoteDiscountLabel ||
    ''
  ).trim() || 'Discount';

const quoteAdditionalDiscountLabel = (quote) =>
  String(
    quote?.additionalDiscountLabel ||
    (quote?.items || []).find((item) => item?._additionalDiscountLabel)?._additionalDiscountLabel ||
    ''
  ).trim() || 'Additional Discount';

/** Primary row label: title when set, otherwise legacy P-…/Q-… or id. */
function quoteListPrimaryLabel(q) {
  const t = String(q?.title || "").trim();
  if (t) return t;
  if (q?.quoteCode) {
    const prefix = q.client?.name ? `P-${String(q._id || "").slice(-4).toUpperCase()}` : "";
    return prefix ? `${prefix}/${q.quoteCode}` : String(q.quoteCode);
  }
  return String(q?._id || "").slice(-8) || "Quote";
}

/** Quotation reference for subtitle when title is shown. */
function quoteListCodeLine(q) {
  if (!q?.quoteCode) return "";
  const prefix = q.client?.name ? `P-${String(q._id || "").slice(-4).toUpperCase()}` : "";
  return prefix ? `${prefix}/${q.quoteCode}` : String(q.quoteCode);
}

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false}) : '—';
function nextAvailableQuoteTitle(baseTitle, existingQuotes = []) {
  const cleanBase = String(baseTitle || "Quotation").trim() || "Quotation";
  const usedTitles = new Set(existingQuotes.map((q) => String(q?.title || "").trim().toLowerCase()).filter(Boolean));
  if (!usedTitles.has(cleanBase.toLowerCase())) return cleanBase;

  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${cleanBase} ${index}`;
    if (!usedTitles.has(candidate.toLowerCase())) return candidate;
  }

  return `${cleanBase} ${Date.now()}`;
}

function duplicateQuoteTitle(quote, existingQuotes = []) {
  const bhk = String(quote?.bhkType || "").trim();
  const originalTitle = quoteListPrimaryLabel(quote)
    .replace(/\s+copy(?:\s+\d+)?$/i, "")
    .replace(/\s+\d+$/i, "")
    .trim();
  const baseTitle = quote?.isTemplateQuote && bhk
    ? `${bhk} — revised quotation`
    : `${originalTitle || "Quotation"} — revised`;

  return nextAvailableQuoteTitle(baseTitle, existingQuotes);
}

function isRevisedQuote(q) {
  return /\brevised\b/i.test(String(q?.title || ""));
}

const templateLineKey = (item) =>
  [
    item?.section || item?.space || '',
    item?.itemName || item?.name || '',
    Number(item?.length) || 0,
    Number(item?.height) || 0,
    Number(item?.rate) || 0,
    item?.discountType || '',
    Number(item?.discount) || 0,
    item?.category || item?.quoteMaterial || '',
  ].join('|').toLowerCase();

function hasSameTemplateLines(savedItems = [], templateItems = []) {
  if (!savedItems.length || savedItems.length !== templateItems.length) return false;
  const savedKeys = new Set(savedItems.map(templateLineKey));
  if (savedKeys.size !== savedItems.length) return false;
  return templateItems.every((item) => savedKeys.has(templateLineKey(item)));
}

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000';
/** Pre-built PDF quotations (served from API: /quote-templates/*) */
const BHK_PREBUILT_TEMPLATES = [
  { bhkType: '1 BHK', templatePdfPath: '/quote-templates/1bhk.pdf', title: '1 BHK — pre-built quotation' },
  { bhkType: '2 BHK', templatePdfPath: '/quote-templates/2bhk.pdf', title: '2 BHK — pre-built quotation' },
  { bhkType: '3 BHK', templatePdfPath: '/quote-templates/3bhk.pdf', title: '3 BHK — pre-built quotation' },
];

export default function QuotesTab({ clientId, projectId, isConverted }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotes, setQuotes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);
  const [activeSubTab, setActiveSubTab] = useState({});
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [limit, setLimit]         = useState(10);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [copyLinkLoading, setCopyLinkLoading] = useState(null);
  const [whatsAppLoading, setWhatsAppLoading] = useState(null);
  const [duplicatingQuoteId, setDuplicatingQuoteId] = useState(null);
  const [newQuoteMenuOpen, setNewQuoteMenuOpen] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const newQuoteMenuRef = useRef(null);
  const [quoteRowMenuId, setQuoteRowMenuId] = useState(null);
  const [quotesListSearch, setQuotesListSearch] = useState('');
  const [lineFiltersByQuote, setLineFiltersByQuote] = useState({});

  const patchQuoteLineFilters = (quoteId, patch) => {
    setLineFiltersByQuote((prev) => ({
      ...prev,
      [quoteId]: { ...emptyQuoteLineFilters(), ...(prev[quoteId] || {}), ...patch },
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, page };
      if (clientId)  params.client  = clientId;
      if (projectId) params.project = projectId;
      const r = await getQuotesApi(params);
      setQuotes((r.data.data.quotes || []).map((q) => ({ ...q, items: normalizeQuoteItems(q.items || []) })));
      setTotal(r.data.data.pagination?.total || 0);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientId, projectId, page, limit]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!newQuoteMenuOpen) return;
    const close = (e) => {
      if (newQuoteMenuRef.current && !newQuoteMenuRef.current.contains(e.target)) {
        setNewQuoteMenuOpen(false);
      }
    };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', close);
    };
  }, [newQuoteMenuOpen]);

  useEffect(() => {
    if (!quoteRowMenuId) return;
    const close = (e) => {
      const root = document.querySelector(`[data-quote-menu="${quoteRowMenuId}"]`);
      if (root && !root.contains(e.target)) setQuoteRowMenuId(null);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', close);
    };
  }, [quoteRowMenuId]);

  const goBlankQuoteEditor = () => {
    setNewQuoteMenuOpen(false);
    navigate(`/quote-editor?client=${clientId}${projectId ? `&project=${projectId}` : ''}`, {
      state: { from: location.pathname + location.search },
    });
  };

  const createPrebuiltQuote = async (tpl) => {
  if (!clientId) {
    alert("Missing client for this quote.");
    return;
  }

  setCreatingTemplate(true);

  try {
    // load template items
    const listRes = await api.get("/item-master", {
      params: { templateBhk: tpl.bhkType, limit: 500, page: 1 }
    });

    const masterRows = listRes.data?.data?.items || [];

    if (!masterRows.length) {
      alert("No template items found.");
      return;
    }

    const rawItems = mapItemMasterRowsToQuotePayload(masterRows).filter(
      (item) => String(item.section || item.space || '').trim().toLowerCase() !== 'other'
    );

const uniqueMap = new Map();

rawItems.forEach(item => {
  const key =
    (item.itemName || '') +
    (item.section || '') +
    (item.length || '') +
    (item.height || '');

  if (!uniqueMap.has(key)) {
    uniqueMap.set(key, item);
  }
});

const rawPrebuiltItems = Array.from(uniqueMap.values());
    const items = rawPrebuiltItems.map((it, idx) => (idx === 0 ? { ...it, _selectedTier: 'signature' } : it));

    const res = await createQuoteApi({
      client: clientId,
      project: projectId || undefined,
      title: tpl.title,
      items,
      discount: 0,
      additionalDiscount: 0,
      isTemplateQuote: true,
      templatePdfPath: tpl.templatePdfPath,
      bhkType: tpl.bhkType,
      selectedTier: 'signature',
      packageTier: 'signature'
    });

    const newId = res.data?.data?.quote?._id;

    setNewQuoteMenuOpen(false);
    await load();

    if (newId) {
      navigate(`/quote-editor/${newId}?client=${clientId}${projectId ? `&project=${projectId}` : ''}`);
    }

  } catch (e) {
    alert(e.response?.data?.message || e.message || "Could not create quote");
  } finally {
    setCreatingTemplate(false);
  }
};

  const withCompleteTemplateRowsForPdf = async (quote) => {
    return {
      ...quote,
      items: normalizeQuoteItems(quote.items || []),
    };
  };

  const handlePDF = async (quote) => {
    setPdfLoading(quote._id);
    try {
      const fresh = quote?._id
        ? (await getQuoteApi(quote._id, {
            params: { pdfTs: Date.now() },
          })).data?.data?.quote || quote
        : quote;
      await generateQuotePDF(await withCompleteTemplateRowsForPdf(fresh));
    }
    catch(e) { alert('PDF generation failed: '+e.message); }
    finally { setPdfLoading(null); }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const handleCopyQuoteLink = async (quote) => {
    if (!quote?._id) return;
    setCopyLinkLoading(quote._id);
    try {
      const fresh = (await getQuoteApi(quote._id, {
        params: { pdfTs: Date.now() },
      })).data?.data?.quote || quote;
      const normalizedQuote = await withCompleteTemplateRowsForPdf(fresh);
      const pdfBlob = await generateQuotePDFBlob(normalizedQuote);
      const formData = new FormData();
      const clientNameForFile = String(normalizedQuote.client?.name || 'Client').replace(/[\\/:*?"<>|]/g, '-');
      formData.append('pdf', pdfBlob, `Quotation_${clientNameForFile}.pdf`);

      const res = await uploadQuoteWhatsAppPdfApi(quote._id, formData);
      const pdfUrl = res.data?.data?.pdfUrl;
      if (!pdfUrl) throw new Error('Quotation link is not available');
      await copyTextToClipboard(pdfUrl);
      alert('Quotation link copied.');
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Could not copy quotation link');
    } finally {
      setCopyLinkLoading(null);
    }
  };

  const handleSendWhatsApp = async (quote) => {
    if (!quote?._id) return;
    const whatsAppWindow = window.open('', '_blank');
    if (whatsAppWindow) {
      whatsAppWindow.document.write('<p style="font-family: sans-serif; padding: 16px;">Preparing WhatsApp message...</p>');
      whatsAppWindow.opener = null;
    }
    setWhatsAppLoading(quote._id);
    try {
      const fresh = (await getQuoteApi(quote._id, {
        params: { pdfTs: Date.now() },
      })).data?.data?.quote || quote;
      const normalizedQuote = await withCompleteTemplateRowsForPdf(fresh);
      const pdfBlob = await generateQuotePDFBlob(normalizedQuote);
      const formData = new FormData();
      const clientNameForFile = String(normalizedQuote.client?.name || 'Client').replace(/[\\/:*?"<>|]/g, '-');
      formData.append('pdf', pdfBlob, `Quotation_${clientNameForFile}.pdf`);

      const res = await uploadQuoteWhatsAppPdfApi(quote._id, formData);
      const data = res.data?.data || {};
      const phoneDigits = String(data.phoneNumber || '').replace(/\D/g, '');
      const phoneNumber = phoneDigits.length > 10 ? phoneDigits.slice(-10) : phoneDigits;
      const clientName = data.clientName || normalizedQuote.client?.name || 'Client';
      const pdfUrl = data.pdfUrl;

      if (!phoneNumber || !pdfUrl) {
        throw new Error('Missing WhatsApp phone number or quotation PDF link');
      }

      const message = `Hello ${clientName}, please find your quotation attached:\n${pdfUrl}`;
      const whatsappUrl = `https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`;
      if (whatsAppWindow && !whatsAppWindow.closed) {
        whatsAppWindow.location.href = whatsappUrl;
      } else {
        window.location.href = whatsappUrl;
      }
    } catch (e) {
      if (whatsAppWindow && !whatsAppWindow.closed) whatsAppWindow.close();
      alert(e.response?.data?.message || e.message || 'Could not prepare WhatsApp message');
    } finally {
      setWhatsAppLoading(null);
    }
  };

  const handleDuplicate = async (quote) => {
    if (!quote?._id) return;
    setDuplicatingQuoteId(quote._id);
    try {
      const copyTitle = duplicateQuoteTitle(quote, quotes);
      const clonedItems = normalizeQuoteItems(quote.items || []).map(({ _id, item, _additionalDiscount, ...rest }) => ({
        item: typeof item === 'object' && item !== null ? item._id : item,
        ...rest,
      }));
      const payload = {
        client: typeof quote.client === 'object' && quote.client !== null ? quote.client._id : quote.client || clientId,
        project: typeof quote.project === 'object' && quote.project !== null ? quote.project._id : quote.project || projectId || undefined,
        title: copyTitle,
        status: 'draft',
        items: clonedItems,
        discount: Number(quote.discount) || 0,
        additionalDiscount: Number(quote.additionalDiscount) || 0,
        discountLabel: quoteDiscountLabel(quote),
        charges: Array.isArray(quote.charges) ? quote.charges.map((c) => ({ ...c })) : [],
        termsAndConditions: quote.termsAndConditions || undefined,
        remarks: quote.remarks || '',
        files: Array.isArray(quote.files) ? [...quote.files] : [],
        isTemplateQuote: !!quote.isTemplateQuote,
        templatePdfPath: quote.templatePdfPath || undefined,
        bhkType: quote.bhkType || undefined,
      };
      const res = await createQuoteApi(payload);
      const newId = res.data?.data?.quote?._id;
      await load();
      if (newId) {
        setExpanded(newId);
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Could not duplicate quote');
    } finally {
      setDuplicatingQuoteId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    await deleteQuoteApi(id);
    load();
  };

  const getSubTab = (id) => activeSubTab[id] || 'section_summary';
  const setSubTab = (id, tab) => setActiveSubTab(s => ({...s, [id]: tab}));

  const calcQuoteTotals = (quote, items = null) => getQuoteCommercialTotals(quote, items);

  // Section summary from items (aligned with line amounts / editor qty rules)
  const buildSectionSummary = (items = []) => {
    const secs = {};
    items.forEach((it) => {
      const s = it.section || it.space || 'No Section';
      if (!secs[s]) secs[s] = { items: 0, baseTotal: 0, discount: 0, subTotal: 0 };
      const base = quoteLineBase(it);
      const disc = quoteLineDiscountAmount(it);
      const net = quoteLineNetBeforeGst(it);
      secs[s].items += 1;
      secs[s].baseTotal += base;
      secs[s].discount += disc;
      secs[s].subTotal += net;
    });
    return secs;
  };

  const thS = { padding:'10px 12px',fontSize:11,fontWeight:700,color:'#fff',background:'#2e1a46',borderBottom:'1px solid #2e1a46',borderRight:'1px solid rgba(255,255,255,0.14)',textAlign:'left',whiteSpace:'nowrap',verticalAlign:'middle' };
  const thFilterS = { ...thS, paddingTop:6, paddingBottom:8, color:'#6B7280', background:'#FAFAFA', borderBottom:'1px solid #E5E7EB', borderRight:'1px solid #EEF2F7' };
  const tdS = { padding:'10px 12px',fontSize:12,color:'#374151',borderBottom:'1px solid #E5E7EB',borderRight:'1px solid #F1F5F9',verticalAlign:'middle',lineHeight:1.35 };

  const filteredQuotes = useMemo(() => {
    const s = quotesListSearch.trim();
    if (!s) return quotes;
    return quotes.filter((q) => quoteCardMatch(q, s));
  }, [quotes, quotesListSearch]);

  if (loading) return <div style={{ textAlign:'center',padding:'4rem',color:'#9CA3AF' }}>Loading quotes…</div>;

  return (
    <div className="quotes-tab-mobile">
      <TableSearchInput
        value={quotesListSearch}
        onChange={(e) => setQuotesListSearch(e.target.value)}
        placeholder="Search quotes (title, code, status, BHK…)"
        style={{ maxWidth: '100%', marginBottom: 14 }}
      />
      {/* + Quote — blank editor or pre-built 1/2/3 BHK PDF templates */}
      <div ref={newQuoteMenuRef} className="quotes-mobile-actions" style={{ display:'flex',justifyContent:'flex-end',marginBottom:14,gap:8,position:'relative' }}>
        <div style={{ position:'relative' }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setNewQuoteMenuOpen((o) => !o); }}
            disabled={creatingTemplate}
            style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:creatingTemplate?'wait':'pointer',opacity:creatingTemplate?0.85:1 }}
          >
            + Quote
            <span style={{ fontSize:10,marginLeft:2 }}>▾</span>
          </button>
          {newQuoteMenuOpen && (
            <div
              style={{
                position:'absolute',right:0,top:'100%',marginTop:6,minWidth:240,
                background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,boxShadow:'0 10px 40px rgba(0,0,0,0.12)',zIndex:50,overflow:'hidden',
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={goBlankQuoteEditor}
                style={{ display:'block',width:'100%',textAlign:'left',padding:'10px 14px',border:'none',background:'transparent',fontSize:13,cursor:'pointer',color:'#1F2937',fontWeight:600 }}
              >
                Blank quote (line items)
              </button>
              <div style={{ height:1,background:'#F3F4F6' }} />
              <div style={{ padding:'6px 12px',fontSize:10,fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.06em' }}>Pre-built PDF</div>
              {BHK_PREBUILT_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.bhkType}
                  type="button"
                  disabled={creatingTemplate}
                  onClick={() => createPrebuiltQuote(tpl)}
                  style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 14px 10px',border:'none',background:'transparent',fontSize:13,cursor:creatingTemplate?'wait':'pointer',color:'#374151' }}
                >
                  {tpl.bhkType} template
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setNewQuoteMenuOpen((o) => !o); }}
          style={{ width:32,height:32,border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#6B7280' }}
          title="New quote options"
        >
          ⋮
        </button>
      </div>

      {/* Quote rows */}
      {quotes.length === 0 ? (
        <div style={{ textAlign:'center',padding:'4rem',color:'#9CA3AF',border:'1px solid #E5E7EB',borderRadius:10 }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin:'0 auto 10px',display:'block' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          No quotes yet
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div style={{ textAlign:'center',padding:'3rem',color:'#9CA3AF',border:'1px solid #E5E7EB',borderRadius:10,fontSize:13 }}>
          No quotes match your search
        </div>
      ) : filteredQuotes.map((q, quoteIndex) => {
        const revised = isRevisedQuote(q);
        return (
        <div
          className="quote-mobile-card"
          key={q._id}
          style={{
            border: revised ? '1px solid #D8B4D6' : '1px solid #E5E7EB',
            borderLeft: revised ? '4px solid #976793' : '1px solid #E5E7EB',
            borderRadius:10,
            marginBottom:10,
            overflow:'hidden',
            boxShadow: revised ? '0 1px 0 rgba(151,103,147,0.08)' : 'none',
          }}
        >
          {/* Quote Row Header */}
          {(() => {
            const totals = calcQuoteTotals(q);
            const displayTotal = totals.total;
            return (
          <div className="quote-mobile-header" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:'#fff',cursor:'pointer' }}
            onClick={() => setExpanded(e => e === q._id ? null : q._id)}>
            <div className="quote-mobile-main" style={{ display:'flex',alignItems:'center',gap:10 }}>
              {/* Orange document icon */}
              <div style={{ width:32,height:32,borderRadius:6,background:'#FFF7ED',border:'1px solid #FED7AA',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <div>
                <div className="quote-mobile-badges" style={{ display:'flex',alignItems:'center',gap:8 }}>
                  <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>
                    {quoteListPrimaryLabel(q)}
                  </span>
                  {q.isTemplateQuote && (
                    <span style={{ background:'#F7F1F7',color:'#976793',padding:'1px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>
                      Template
                    </span>
                  )}
                  {q.bhkType && <span style={{ background:'#F7F1F7',color:'#976793',padding:'1px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>{q.bhkType}</span>}
                  {(q.selectedTier || q.packageTier || q.items?.[0]?._selectedTier) && (
                    <span style={{ background:'#F3EEF6',color:'#2E1A46',padding:'1px 8px',borderRadius:20,fontSize:10,fontWeight:700,textTransform:'capitalize' }}>
                      {q.selectedTier || q.packageTier || q.items?.[0]?._selectedTier}
                    </span>
                  )}
                  {revised && (
                    <span style={{ background:'#FFF7ED',color:'#C2410C',padding:'1px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>
                      Revised
                    </span>
                  )}
                  <span style={{ background: q.status==='approved'?'#D1FAE5':q.status==='sent'?'#DBEAFE':'#F3F4F6', color: q.status==='approved'?'#059669':q.status==='sent'?'#976793':'#6B7280', padding:'2px 10px',borderRadius:20,fontSize:10,fontWeight:600,textTransform:'capitalize' }}>
                    {q.status}
                  </span>
                  {quoteIndex === 0 && (
                    <span style={{ background: '#ECFDF5', color: '#047857', padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>
                      Latest
                    </span>
                  )}
                  {/* Duplicate icon */}
                  <button onClick={e=>e.stopPropagation()} style={{ background:'none',border:'none',cursor:'pointer',color:'#9CA3AF' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                </div>
                <div style={{ fontSize:11,color:'#9CA3AF',marginTop:2 }}>
                  {String(q.title || "").trim() && quoteListCodeLine(q)
                    ? `${quoteListCodeLine(q)} · `
                    : ""}
                  Last updated: {fmt(q.updatedAt)}
                </div>
              </div>
            </div>
            <div className="quote-mobile-total" style={{ display:'flex',alignItems:'center',gap:10 }}>
              <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>
                {displayTotal > 0 ? `₹ ${formatInr(displayTotal)}` : '₹ 0.00'}
              </span>
              <span style={{ color:'#9CA3AF',fontSize:16 }}>{expanded===q._id ? '▲':'▼'}</span>
            </div>
          </div>
            );
          })()}

          {/* Expanded Content */}
          {expanded === q._id && (
            <div style={{ borderTop:'1px solid #E5E7EB' }}>
              {/* Edit / PDF buttons */}
              <div className="quote-expanded-actions" style={{ display:'flex',alignItems:'center',justifyContent:'flex-end',gap:8,padding:'10px 16px',background:'#FAFAFA',borderBottom:'1px solid #E5E7EB',position:'relative' }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/quote-editor/${q._id}?client=${clientId}${projectId ? `&project=${projectId}` : ''}`, { state: { from: location.pathname + location.search } }); }}
                  style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',border:'1px solid #976793',borderRadius:8,background:'#fff',color:'#976793',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); handlePDF(q); }} disabled={pdfLoading===q._id}
                  style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',border:'1px solid #EF4444',borderRadius:8,background:'#fff',color:'#EF4444',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  {pdfLoading===q._id ? 'Generating…' : 'PDF'}
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); handleCopyQuoteLink(q); }} disabled={copyLinkLoading===q._id}
                  style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',border:'1px solid #2563EB',borderRadius:8,background:'#fff',color:'#2563EB',fontSize:12,fontWeight:600,cursor:copyLinkLoading===q._id?'wait':'pointer',opacity:copyLinkLoading===q._id?0.8:1 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  {copyLinkLoading===q._id ? 'Copying...' : 'Copy Link'}
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); handleSendWhatsApp(q); }} disabled={whatsAppLoading===q._id}
                  style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',border:'1px solid #16A34A',borderRadius:8,background:'#fff',color:'#16A34A',fontSize:12,fontWeight:600,cursor:whatsAppLoading===q._id?'wait':'pointer',opacity:whatsAppLoading===q._id?0.8:1 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm0 18.16h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.13.82.84-3.05-.2-.31a8.18 8.18 0 0 1-1.25-4.39c0-4.54 3.69-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.25-8.24 8.25Zm4.51-6.17c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.04s.89 2.37 1.02 2.53c.12.17 1.76 2.69 4.26 3.77.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28Z"/></svg>
                  {whatsAppLoading===q._id ? 'Preparing...' : 'WhatsApp'}
                </button>
                <div style={{ position:'relative' }} data-quote-menu={q._id}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuoteRowMenuId((id) => (id === q._id ? null : q._id)); }}
                    style={{ width:28,height:28,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}
                    title="More"
                  >
                    ⋮
                  </button>
                  {quoteRowMenuId === q._id && (
                    <div
                      role="menu"
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ position:'absolute',right:0,top:'100%',marginTop:4,minWidth:160,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',zIndex:80,overflow:'hidden' }}
                    >
                      <button
                        type="button"
                        onClick={() => { handlePDF(q); setQuoteRowMenuId(null); }}
                        disabled={pdfLoading === q._id}
                        style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 12px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#374151' }}
                      >
                        Download quotation PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => { handleDuplicate(q); setQuoteRowMenuId(null); }}
                        disabled={duplicatingQuoteId === q._id}
                        style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 12px',border:'none',background:'transparent',fontSize:12,cursor:duplicatingQuoteId===q._id?'wait':'pointer',color:'#374151' }}
                      >
                        {duplicatingQuoteId === q._id ? 'Duplicating quote...' : 'Duplicate quote'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (window.confirm('Delete this quote?')) { handleDelete(q._id); setExpanded((ex) => (ex === q._id ? null : ex)); } setQuoteRowMenuId(null); }}
                        style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 12px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#EF4444' }}
                      >
                        Delete quote
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Sub tabs */}
              <div className="quote-subtabs" style={{ display:'flex',borderBottom:'1px solid #E5E7EB',padding:'0 16px',background:'#fff' }}>
                {['section_summary','items'].map(tab => (
                  <button key={tab} onClick={()=>setSubTab(q._id,tab)}
                    style={{ padding:'8px 14px',fontSize:12,fontWeight:getSubTab(q._id)===tab?600:400,color:getSubTab(q._id)===tab?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:getSubTab(q._id)===tab?'2px solid #976793':'2px solid transparent',cursor:'pointer',whiteSpace:'nowrap',marginBottom:-1 }}>
                    {tab === 'section_summary' ? 'Section Summary' : 'Items'}
                  </button>
                ))}
              </div>

              {/* Section Summary */}
              {getSubTab(q._id) === 'section_summary' && (
                <div style={{ padding:'0 16px 16px' }}>
                  {(() => {
                    const lf = lineFiltersByQuote[q._id] || emptyQuoteLineFilters();
                    const baseItems = activeQuoteItems(q.items || []);
                    const scopedItems = baseItems.filter((it) => matchesItemSpaceMaterialLine(it, lf));
                    const totals = scopedItems.length === baseItems.length
                      ? calcQuoteTotals(q)
                      : getQuoteCommercialTotals({ ...q, discount: 0, total: 0, charges: [] }, scopedItems);
                    const secMap = buildSectionSummary(scopedItems);
                    const sl = normalizeSearch(lf.sectionLabel);
                    const secEntries = Object.entries(secMap).filter(([sec]) => !sl || String(sec).toLowerCase().includes(sl));
                    return (
                    <>
                  <div style={{ marginTop:12,padding:'10px 12px',background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8,fontSize:11,color:'#6B7280' }}>
                    <div style={{ fontWeight:600,marginBottom:8,color:'#374151' }}>Scope lines for this summary</div>
                    <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:10 }}>
                      <div>
                        <span style={{ fontSize:10,fontWeight:600 }}>Item</span>
                        <ColumnFilterInput value={lf.item} onChange={(e) => patchQuoteLineFilters(q._id, { item: e.target.value })} placeholder="Filter item…" aria-label="Filter lines by item" />
                      </div>
                      <div>
                        <span style={{ fontSize:10,fontWeight:600 }}>Space</span>
                        <ColumnFilterInput value={lf.space} onChange={(e) => patchQuoteLineFilters(q._id, { space: e.target.value })} placeholder="Filter space…" aria-label="Filter lines by space" />
                      </div>
                      <div>
                        <span style={{ fontSize:10,fontWeight:600 }}>Material</span>
                        <ColumnFilterInput value={lf.material} onChange={(e) => patchQuoteLineFilters(q._id, { material: e.target.value })} placeholder="Filter material…" aria-label="Filter lines by material" />
                      </div>
                    </div>
                  </div>
                  <table style={{ width:'100%',borderCollapse:'collapse',marginTop:10 }}>
                    <thead>
                      <tr>
                        <th style={thS}>Section</th>
                        <th style={thS}>Items</th>
                        <th style={thS}>Base Total</th>
                        <th style={thS}>Discount</th>
                        <th style={{...thS,textAlign:'right'}}>Sub Total</th>
                      </tr>
                      <tr style={{ background:'#FAFAFA' }}>
                        <th style={{ ...thFilterS, verticalAlign:'top' }}>
                          <ColumnFilterInput value={lf.sectionLabel} onChange={(e) => patchQuoteLineFilters(q._id, { sectionLabel: e.target.value })} placeholder="Filter section…" aria-label="Filter section row" />
                        </th>
                        <th style={thFilterS} colSpan={4} />
                      </tr>
                    </thead>
                    <tbody>
                      {secEntries.map(([sec,d])=>(
                        <tr key={sec}>
                          <td style={tdS}>{sec}</td>
                          <td style={tdS}>{d.items}</td>
                          <td style={tdS}>₹ {formatInr(d.baseTotal)}</td>
                          <td style={tdS}>₹ {formatInr(d.discount)}</td>
                          <td style={{...tdS,textAlign:'right'}}>₹ {formatInr(d.subTotal)}</td>
                        </tr>
                      ))}
                      {scopedItems.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>{baseItems.length === 0 ? 'No items' : 'No lines match filters'}</td></tr>
                      )}
                      {scopedItems.length > 0 && secEntries.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No sections match filters</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div style={{ textAlign:'right',padding:'12px 0 0',fontSize:12,color:'#6B7280' }}>
                    <div>Sub Total: <strong>₹ {formatInr(totals.baseSubTotal)}</strong></div>
                    <div>{quoteDiscountLabel(q)}: <strong>₹ {formatInr(totals.discountTotal)}</strong></div>
                    {totals.additionalDiscount > 0 && (
                      <div>{quoteAdditionalDiscountLabel(q)}: <strong>₹ {formatInr(totals.additionalDiscount)}</strong></div>
                    )}
                    <div style={{ fontWeight:700,fontSize:13,color:'#1F2937' }}>Total: ₹ {formatInr(totals.total)}</div>
                  </div>
                    </>
                    );
                  })()}
                </div>
              )}

              {/* Items Tab */}
              {getSubTab(q._id) === 'items' && (
                <div style={{ padding:'0 16px 16px',overflowX:'auto' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse',marginTop:12,minWidth:980 }}>
                    <thead>
                      <tr>
                      {['Sr.No.','Item','Space','Material','L','H','Qty','UOM','Rate','Discount','Amount','Discounted Amount'].map((h) => (
                        <th key={h} style={h === 'Amount' || h === 'Discounted Amount' ? { ...thS, textAlign: 'right' } : h === 'Sr.No.' ? { ...thS, textAlign:'center' } : thS}>{h}</th>
                      ))}
                      </tr>
                      <tr style={{ background:'#FAFAFA' }}>
                        <th style={{ ...thFilterS, textAlign:'center' }} />
                        {(() => {
                          const lf = lineFiltersByQuote[q._id] || emptyQuoteLineFilters();
                          return (
                            <>
                                <th style={{ ...thFilterS, verticalAlign:'top' }}>
                                <ColumnFilterInput value={lf.item} onChange={(e) => patchQuoteLineFilters(q._id, { item: e.target.value })} placeholder="Filter item…" aria-label="Filter by item" />
                              </th>
                                <th style={{ ...thFilterS, verticalAlign:'top' }}>
                                <ColumnFilterInput value={lf.space} onChange={(e) => patchQuoteLineFilters(q._id, { space: e.target.value })} placeholder="Filter space…" aria-label="Filter by space" />
                              </th>
                                <th style={{ ...thFilterS, verticalAlign:'top' }}>
                                <ColumnFilterInput value={lf.material} onChange={(e) => patchQuoteLineFilters(q._id, { material: e.target.value })} placeholder="Filter material…" aria-label="Filter by material" />
                              </th>
                              <th style={thFilterS} colSpan={8} />
                            </>
                          );
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const lf = lineFiltersByQuote[q._id] || emptyQuoteLineFilters();
                        const all = activeQuoteItems(q.items || []);
                        const rows = all.filter((it) => matchesItemSpaceMaterialLine(it, lf));
                        if (all.length === 0) {
                          return <tr><td colSpan={12} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No items</td></tr>;
                        }
                        if (rows.length === 0) {
                          return <tr><td colSpan={12} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No lines match filters</td></tr>;
                        }
                        return rows.map((it,i)=>{
                        const qty = effectiveQuoteLineQty(it);
                        return (
                        <tr key={it._id||i}>
                          <td style={{ ...tdS, textAlign:'center', fontWeight:700 }}>{i+1}</td>
                          <td style={{ ...tdS, maxWidth:200 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                              <div style={{ width:36,height:36,borderRadius:6,border:'1px solid #E5E7EB',background:'#F3F4F6',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden' }}>
                                {it.image ? <img src={`${BASE_URL}${it.image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>}
                              </div>
                              <div>
                                <div style={{ fontWeight:600,fontSize:12,color:'#1F2937' }}>{it.itemName||'—'}</div>
                                {it.itemDetails ? <div style={{ fontSize:11,color:'#6B7280',marginTop:2 }}>{it.itemDetails}</div> : null}
                              </div>
                            </div>
                          </td>
                          <td style={tdS}>{it.section || it.space || '—'}</td>
                          <td style={tdS}>{it.category||'—'}</td>
                          <td style={tdS}>{it.length??'—'}</td>
                          <td style={tdS}>{it.height??'—'}</td>
                          <td style={tdS}>{qty ? qty.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}</td>
                          <td style={tdS}>{it.uom || '—'}</td>
                          <td style={tdS}>₹ {formatInr(it.rate || 0)}</td>
                          <td style={tdS}>{it.discountType === '₹' ? `₹ ${formatInr(it.discount || 0)}` : `% ${it.discount||0}`}</td>
                          <td style={{...tdS,fontWeight:500,color:'#374151',textAlign:'right'}}>₹ {formatInr(quoteLineBase(it))}</td>
                          <td style={{...tdS,fontWeight:600,color:'#1F2937',textAlign:'right'}}>
                            <div>₹ {formatInr(quoteLineNetBeforeGst(it))}</div>
                            {quoteLineDiscountAmount(it) > 0 && (
                              <div style={{ fontSize: 10, color: '#059669', fontWeight: 500, marginTop: 1 }}>
                                (-₹ {formatInr(quoteLineDiscountAmount(it))})
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        );
      })}

      {/* Pagination */}
      {total > 0 && (
        <div className="quotes-pagination" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',fontSize:12,color:'#6B7280' }}>
          <span>Showing 1 to {Math.min(page*limit,total)} of {total} items</span>
          <div style={{ display:'flex',alignItems:'center',gap:6 }}>
            <span>Items per page:</span>
            <select value={limit} onChange={e=>{setLimit(Number(e.target.value));setPage(1);}}
              style={{ padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,outline:'none' }}>
              {[10,25,50].map(n=><option key={n} value={n}>{n}</option>)}
            </select>
            <button disabled={page===1} onClick={()=>setPage(p=>p-1)} style={{ width:26,height:26,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:page===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:page===1?'#D1D5DB':'#374151' }}>‹</button>
            <span>{page}</span>
            <button disabled={page*limit>=total} onClick={()=>setPage(p=>p+1)} style={{ width:26,height:26,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:page*limit>=total?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:page*limit>=total?'#D1D5DB':'#374151' }}>›</button>
          </div>
        </div>
      )}
    </div>
  );
}
